/**
 * Charm Builder — Klaviyo events + abandonment sync
 * PRD: docs/prd/awe-charm-builder-abandon-prd.md
 *
 * Events (klaviyo.track) — names/properties shared with Marketing's flows:
 *   - "Viewed Charm Builder"          once per page load
 *   - "Charm Builder Base Selected"   shopper picks a different base
 *   - "Charm Builder Charm Added"     shopper adds a charm (one per variant)
 *   - "Charm Builder Review Viewed"   shopper reaches the review step
 *   - "Charm Builder Added to Cart"   set added to cart
 *   - "Charm Builder Started"         first charm of a build, max once / 24h
 * Every event carries DeviceType and BuilderSessionID. Image fields are the
 * product image URL Shopify serialises into the builder data (featuredImage,
 * 800px), made absolute with https: — Shopify gives it protocol-relative
 * ("//…/cdn/shop/…"), which email clients don't load.
 *
 * Profile properties (klaviyo.identify), always the latest build:
 *   cb_restore_url, cb_status, cb_base_title, cb_base_material,
 *   cb_charm_count, cb_subtotal, cb_currency, cb_updated_at.
 *
 * Only state changes made by the shopper after the builder has initialised
 * count as Base Selected / Charm Added — the auto-selected default base, the
 * PDP ?cb_base= hand-off and restore-link charms are all applied during init
 * and are deliberately not reported.
 *
 * Only ever talks to Klaviyo for a known profile: a logged-in customer
 * (identified by email from Liquid, before any event) or a visitor klaviyo.js
 * already recognises. Anonymous visitors are skipped.
 *
 * Consent: off by default (sync for everyone). When the theme setting
 * awe_cb_klaviyo_require_consent is on, nothing is sent until Shopify's
 * Customer Privacy API reports marketing consent.
 *
 * Self-contained like awe-charm-builder-analytics.js: reads CharmBuilder
 * state, listens to 'charm-builder:updated' / 'charm-builder:cart-added',
 * never calls into charm-builder.js. Every failure is swallowed.
 */
(function () {
  const root = document.getElementById("charm-builder");
  if (!root || !window.CharmBuilder || typeof window.CharmBuilder.toRestoreUrl !== "function") return;

  const SYNC_DEBOUNCE_MS = 3000;
  const STARTED_THROTTLE_MS = 24 * 60 * 60 * 1000;
  const REVIEW_STEP = 3;
  const MOBILE_QUERY = "(max-width: 749px)"; // theme's mobile breakpoint
  const LAST_URL_KEY = "awe-cb-klaviyo-last-url";
  const STARTED_AT_KEY = "awe-cb-klaviyo-started-at";
  const SESSION_ID_KEY = "awe-cb-session-id";

  const customerEmail = root.dataset.customerEmail || "";
  const requireConsent = root.dataset.requireConsent === "true";
  const currency = (window.__BUILDER_DATA__ && window.__BUILDER_DATA__.currency) || "USD";

  let syncTimer = null;
  let emailIdentifyPromise = null;
  let ready = false;
  let prev = null;

  // ---------------------------------------------------------------------------
  // Storage — can throw in private mode / blocked storage; never fatal.
  // ---------------------------------------------------------------------------
  function readStore(key, storage = window.localStorage) {
    try {
      return storage.getItem(key);
    } catch (_) {
      return null;
    }
  }

  function writeStore(key, value, storage = window.localStorage) {
    try {
      if (value === null) storage.removeItem(key);
      else storage.setItem(key, value);
    } catch (_) {}
  }

  // ---------------------------------------------------------------------------
  // Klaviyo onsite object (loaded by the Klaviyo app embed). Calls go through
  // window.klaviyo when present; before klaviyo.js loads they queue onto
  // window._klOnsite, which klaviyo.js drains on load. Always resolves.
  // ---------------------------------------------------------------------------
  function kl(method, ...args) {
    return new Promise(resolve => {
      try {
        const k = window.klaviyo;
        if (k && !Array.isArray(k) && typeof k[method] === "function") {
          Promise.resolve(k[method](...args)).then(resolve, () => resolve(undefined));
          return;
        }
        window._klOnsite = window._klOnsite || [];
        window._klOnsite.push([method, ...args, resolve]);
      } catch (_) {
        resolve(undefined);
      }
    });
  }

  // Logged-in customers are identified by email once, before anything else
  // is sent; every caller awaits the same promise so no event can race ahead.
  async function ensureIdentified() {
    if (customerEmail) {
      emailIdentifyPromise = emailIdentifyPromise || kl("identify", { email: customerEmail });
      await emailIdentifyPromise;
      return true;
    }
    return !!(await kl("isIdentified"));
  }

  // ---------------------------------------------------------------------------
  // Consent — only consulted when the theme setting requires it.
  // ---------------------------------------------------------------------------
  function consentGiven() {
    if (!requireConsent) return true;
    const cp = window.Shopify && window.Shopify.customerPrivacy;
    try {
      return !!(cp && typeof cp.marketingAllowed === "function" && cp.marketingAllowed());
    } catch (_) {
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  // Shared event context
  // ---------------------------------------------------------------------------
  function deviceType() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches ? "mobile" : "desktop";
  }

  // One id per browser tab: survives reloads, reset after add to cart so the
  // next build starts a new session.
  function builderSessionId() {
    let id = readStore(SESSION_ID_KEY, window.sessionStorage);
    if (!id) {
      id = `cb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      writeStore(SESSION_ID_KEY, id, window.sessionStorage);
    }
    return id;
  }

  async function track(event, properties) {
    if (!consentGiven()) return;
    if (!(await ensureIdentified())) return;
    await kl(
      "track",
      event,
      Object.assign({}, properties, {
        DeviceType: deviceType(),
        BuilderSessionID: builderSessionId(),
      })
    );
  }

  // ---------------------------------------------------------------------------
  // Data lookups — __CHARM_BUILDER_DATA__.bases / .charms are arrays of
  // { categoryTitle, products }, each product carrying its variants.
  // ---------------------------------------------------------------------------
  function findVariant(pool, variantId) {
    const cats = (window.__CHARM_BUILDER_DATA__ && window.__CHARM_BUILDER_DATA__[pool]) || [];
    for (const cat of cats) {
      for (const p of cat.products || []) {
        const v = (p.variants || []).find(v => v.id === variantId);
        if (v) return v;
      }
    }
    return null;
  }

  function toDollars(cents) {
    return Math.round(cents || 0) / 100;
  }

  function charmCount(state) {
    return (state.charms || []).reduce((sum, c) => sum + Math.max(1, c.quantity || 1), 0);
  }

  // Set value before the charm-count tier discount — base + every charm unit.
  function setValue(state) {
    const cents =
      ((state.base && state.base.price) || 0) +
      (state.charms || []).reduce((sum, c) => sum + (c.price || 0) * Math.max(1, c.quantity || 1), 0);
    return toDollars(cents);
  }

  // Base + charms as they'd go to the cart, for set-level events: charm
  // units grouped by variant so an email can loop over them.
  function setDetails(state) {
    const charms = new Map();
    (state.charms || []).forEach(c => {
      const qty = Math.max(1, c.quantity || 1);
      const entry = charms.get(c.variantId);
      if (entry) {
        entry.Quantity += qty;
        return;
      }
      charms.set(c.variantId, {
        ProductID: c.productId,
        VariantID: String(c.variantId),
        ProductName: c.title || null,
        Price: toDollars(c.price),
        Quantity: qty,
        ImageURL: imageUrl(c.featuredImage),
      });
    });
    return {
      ProductID: state.base.productId,
      ProductName: state.base.title || null,
      BaseImageURL: imageUrl(state.base.featuredImage),
      Charms: Array.from(charms.values()),
      CharmCount: charmCount(state),
    };
  }

  function material(variant) {
    return (variant && variant.options && variant.options.Material) || null;
  }

  // Shopify's image_url is protocol-relative ("//aweinspired.com/cdn/…").
  function imageUrl(url) {
    if (!url) return null;
    return url.startsWith("//") ? `https:${url}` : url;
  }

  function absoluteUrl(path) {
    if (!path) return null;
    return /^https?:\/\//.test(path) ? path : window.location.origin + path;
  }

  function restoreUrl(state) {
    return absoluteUrl(window.CharmBuilder.toRestoreUrl(state));
  }

  function clone(state) {
    return JSON.parse(JSON.stringify(state));
  }

  // ---------------------------------------------------------------------------
  // Events from shopper actions (diffed against the previous state)
  // ---------------------------------------------------------------------------
  function onBaseSelected(state) {
    const base = state.base;
    const variant = findVariant("bases", base.variantId);
    track("Charm Builder Base Selected", {
      ProductID: base.productId,
      VariantID: String(base.variantId),
      ProductName: base.title || null,
      SKU: (variant && variant.sku) || null,
      Price: toDollars(base.price),
      ProductURL: absoluteUrl(base.url),
      Material: material(variant),
      ImageURL: imageUrl(base.featuredImage),
      RestoreURL: restoreUrl(state),
      Currency: currency,
    }).catch(() => {});
  }

  function onCharmsAdded(prevState, state) {
    const before = new Set((prevState.charms || []).map(c => c.instanceId));
    // Group the new records by variant — the sidebar adds several units of
    // one variant in a single update; that's one "added" moment per variant.
    const added = new Map();
    (state.charms || []).forEach(c => {
      if (before.has(c.instanceId)) return;
      const entry = added.get(c.variantId);
      if (entry) entry.quantity += Math.max(1, c.quantity || 1);
      else added.set(c.variantId, { charm: c, quantity: Math.max(1, c.quantity || 1) });
    });

    added.forEach(({ charm, quantity }) => {
      const variant = findVariant("charms", charm.variantId);
      track("Charm Builder Charm Added", {
        ProductID: charm.productId,
        VariantID: String(charm.variantId),
        ProductName: charm.title || null,
        SKU: (variant && variant.sku) || null,
        Price: toDollars(charm.price),
        Quantity: quantity,
        Material: material(variant),
        ImageURL: imageUrl(charm.featuredImage),
        CharmCount: charmCount(state),
        BuildValue: setValue(state),
        RestoreURL: restoreUrl(state),
        Currency: currency,
      }).catch(() => {});
    });
  }

  function onReviewViewed(state) {
    if (!state.base) return;
    track(
      "Charm Builder Review Viewed",
      Object.assign(setDetails(state), {
        BuildValue: setValue(state),
        RestoreURL: restoreUrl(state),
        Currency: currency,
      })
    ).catch(() => {});
  }

  function onUpdated() {
    if (!ready) return; // init-time changes (default base, ?cb_base=, restore link) aren't shopper actions
    const state = window.CharmBuilder.state;
    if (state.base && (!prev.base || prev.base.variantId !== state.base.variantId)) onBaseSelected(state);
    onCharmsAdded(prev, state);
    if (state.step === REVIEW_STEP && prev.step !== REVIEW_STEP) onReviewViewed(state);
    prev = clone(state);
    scheduleSync();
  }

  // ---------------------------------------------------------------------------
  // Profile sync — cb_* properties + (throttled) "Charm Builder Started".
  // ---------------------------------------------------------------------------
  async function sync() {
    const state = window.CharmBuilder.state;
    // A base is auto-selected on every fresh visit, so it alone isn't intent.
    if (!state.base || !charmCount(state)) return;
    if (!consentGiven()) return;

    const url = restoreUrl(state);
    if (!url || readStore(LAST_URL_KEY) === url) return;
    if (!(await ensureIdentified())) return;

    const baseVariant = findVariant("bases", state.base.variantId);
    await kl("identify", {
      cb_restore_url: url,
      cb_status: "in_progress",
      cb_base_title: state.base.title || null,
      cb_base_material: material(baseVariant),
      cb_charm_count: charmCount(state),
      cb_subtotal: setValue(state),
      cb_currency: currency,
      cb_updated_at: new Date().toISOString(),
    });
    writeStore(LAST_URL_KEY, url);

    const startedAt = parseInt(readStore(STARTED_AT_KEY), 10) || 0;
    if (Date.now() - startedAt >= STARTED_THROTTLE_MS) {
      writeStore(STARTED_AT_KEY, String(Date.now()));
      await track(
        "Charm Builder Started",
        Object.assign(setDetails(state), {
          BuildValue: setValue(state),
          RestoreURL: url,
          Currency: currency,
        })
      );
    }
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      sync().catch(() => {});
    }, SYNC_DEBOUNCE_MS);
  }

  // ---------------------------------------------------------------------------
  // Added to cart — flow exit. Runs inside charm-builder-cart.js's
  // waitUntil window so it lands before the redirect to /cart.
  // ---------------------------------------------------------------------------
  async function onCartAdded(state) {
    clearTimeout(syncTimer);
    writeStore(LAST_URL_KEY, null);
    writeStore(STARTED_AT_KEY, null);
    if (!state || !state.base || !consentGiven()) return;
    if (!(await ensureIdentified())) return;

    await Promise.all([
      kl("identify", { cb_status: "carted", cb_updated_at: new Date().toISOString() }),
      track(
        "Charm Builder Added to Cart",
        Object.assign(setDetails(state), {
          CartValue: setValue(state),
          Currency: currency,
        })
      ),
    ]);
    // Next build in this tab is a new session.
    writeStore(SESSION_ID_KEY, null, window.sessionStorage);
  }

  // ---------------------------------------------------------------------------
  // Wiring
  // ---------------------------------------------------------------------------
  document.addEventListener("charm-builder:updated", onUpdated);

  document.addEventListener("charm-builder:cart-added", event => {
    const detail = event.detail || {};
    const done = onCartAdded(detail.state).catch(() => {});
    if (typeof detail.waitUntil === "function") detail.waitUntil(done);
  });

  if (requireConsent) {
    document.addEventListener("visitorConsentCollected", event => {
      if (event.detail && event.detail.marketingAllowed) scheduleSync();
    });
    // The consent banner app normally loads the Customer Privacy API; load it
    // ourselves if it hasn't, so marketingAllowed() is readable.
    if (window.Shopify && !window.Shopify.customerPrivacy && typeof window.Shopify.loadFeatures === "function") {
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], error => {
        if (!error) scheduleSync();
      });
    }
  }

  // charm-builder.js initialises on DOMContentLoaded and this script is
  // deferred after it, so this listener runs once init (restore, sanitise,
  // default base, ?cb_base= / ?cb_charms=) has fully applied.
  function onReady() {
    ready = true;
    prev = clone(window.CharmBuilder.state);
    track("Viewed Charm Builder", {
      PageURL: window.location.href,
      BuilderTitle: (window.__BUILDER_DATA__ && window.__BUILDER_DATA__.title) || null,
    }).catch(() => {});
    // Loading straight onto the review step (reload, ?step=review) is a view too.
    if (prev.step === REVIEW_STEP) onReviewViewed(prev);
    scheduleSync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }
})();
